import { buildSendTemplateSeed } from '@/components/templates/build-send-template-seed';
import {
  useTemplateSelector,
  type AppliedTemplate,
} from '@/components/templates/use-template-selector';
import type {
  BatchAttachmentInfo,
  TemplateLibraryEntry,
  TemplateLibraryLanguage,
  TemplatePair,
} from '@fluenceur/types';
import { useCallback, useMemo, useState } from 'react';

/** Latest editor body, captured for the save-as-template seed. */
export interface EmailEditorSnapshot {
  /** Stringified Lexical JSON of the composed body. */
  lexicalJson: string;
  /** Plain-text rendering of the composed body. */
  plainText: string;
}

interface UseEmailComposerTemplateFlowArgs {
  brandId: string | undefined;
  /** The (channel, context) pair derived from the active send mode. */
  pair: TemplatePair;
  /** Brand send language used when saving a new template from the composition. */
  language: TemplateLibraryLanguage;
  /** Loads a template's Lexical JSON body into the editor (re-seeds the body). */
  onApplyBody: (lexicalJson: string) => void;
  /** Latest editor body, for seeding the save-as-template form. */
  snapshot: EmailEditorSnapshot | null;
  attachments?: BatchAttachmentInfo[];
  onApplyAttachments?: (attachments: BatchAttachmentInfo[]) => void | boolean | Promise<boolean>;
  attachmentsCustomized?: boolean;
  attachmentsBlocked?: boolean;
  attachmentsBusy?: boolean;
}

interface UseEmailComposerTemplateFlowResult {
  entries: TemplateLibraryEntry[];
  isLoading: boolean;
  selectedEntryId: string | null;
  selectedTemplateId: string | null;
  selectEntry: (entryId: string) => void;
  isSaveDrawerOpen: boolean;
  openSaveDrawer: () => void;
  closeSaveDrawer: () => void;
  /** Seed values for the embedded save-as-template form. */
  saveSeed: ReturnType<typeof buildSendTemplateSeed>;
  onTemplateSaved: (entry: TemplateLibraryEntry) => void;
  /** Clear the picker selection when the composer is reset/closed. */
  resetSelection: () => void;
  attachmentsCustomized: boolean;
  attachmentsBlocked: boolean;
  attachmentsBusy: boolean;
}

/**
 * Bridges the generic {@link useTemplateSelector} to the email composer: applying
 * a template re-seeds the Lexical body, and saving builds a seed from the latest
 * editor snapshot. Auto-select is off so the composer opens on a blank message;
 * the user picks a template explicitly. Saving a just-created template
 * auto-selects it once the refreshed library lists it.
 *
 * The selected brand template id is surfaced so invitation outreach can retain
 * its provenance; system defaults deliberately resolve to null.
 */
export const useEmailComposerTemplateFlow = ({
  brandId,
  pair,
  language,
  onApplyBody,
  snapshot,
  attachments,
  onApplyAttachments,
  attachmentsCustomized = false,
  attachmentsBlocked = false,
  attachmentsBusy = false,
}: UseEmailComposerTemplateFlowArgs): UseEmailComposerTemplateFlowResult => {
  const [isSaveDrawerOpen, setIsSaveDrawerOpen] = useState(false);

  const handleTemplateApplied = useCallback(
    (applied: AppliedTemplate) => {
      const applyBody = (accepted: void | boolean) => {
        if (accepted === false) return false;
        if (applied.body) onApplyBody(applied.body);
        return true;
      };
      const result = onApplyAttachments?.(applied.entry.attachments ?? []);
      return result instanceof Promise ? result.then(applyBody) : applyBody(result);
    },
    [onApplyBody, onApplyAttachments]
  );

  const selector = useTemplateSelector({
    brandId,
    pair,
    language,
    includeAllLanguages: true,
    autoSelectDefault: false,
    onTemplateApplied: handleTemplateApplied,
  });

  const { selectWhenAvailable } = selector;

  const openSaveDrawer = useCallback(() => {
    if (!attachmentsBlocked) setIsSaveDrawerOpen(true);
  }, [attachmentsBlocked]);
  const closeSaveDrawer = useCallback(() => setIsSaveDrawerOpen(false), []);

  const onTemplateSaved = useCallback(
    (entry: TemplateLibraryEntry) => {
      // The library cache is invalidated by the create action; select the new
      // entry as soon as the refetch surfaces it.
      selectWhenAvailable(entry.id);
    },
    [selectWhenAvailable]
  );

  const saveSeed = useMemo(
    () =>
      buildSendTemplateSeed({
        pair,
        language,
        content: snapshot?.plainText ?? '',
        messageSnapshot: snapshot?.lexicalJson ?? null,
        attachments,
      }),
    [pair, language, snapshot, attachments]
  );

  return {
    entries: selector.entries,
    isLoading: selector.isLoading,
    selectedEntryId: selector.selectedEntryId,
    selectedTemplateId: selector.selectedTemplateId,
    selectEntry: selector.selectEntry,
    isSaveDrawerOpen,
    openSaveDrawer,
    closeSaveDrawer,
    saveSeed,
    onTemplateSaved,
    resetSelection: selector.resetSelection,
    attachmentsCustomized,
    attachmentsBlocked,
    attachmentsBusy,
  };
};
